import { IStringIdEntity } from './base.interface';
import { ValueOf } from './enums/notificationType.enum';

// =====================================================================
// Campagne notifiche/email gestite dal frontend_admin.
// Owner scritture: backend per CRUD/contenuti (DRAFT/SCHEDULED/CANCELLED);
// notification-service per le SOLE transizioni di run
// (SCHEDULED→RUNNING→COMPLETED|FAILED, startedAt/completedAt/error/stats).
// =====================================================================

export const CampaignStatus = Object.freeze({
	DRAFT: 'DRAFT',
	SCHEDULED: 'SCHEDULED',
	RUNNING: 'RUNNING',
	COMPLETED: 'COMPLETED',
	FAILED: 'FAILED',
	CANCELLED: 'CANCELLED',
});
export type CampaignStatusValues = ValueOf<typeof CampaignStatus>;

export const CampaignTargetType = Object.freeze({
	/** Broadcast sui topic FCM yf-{service|marketing}-{lang} (+ email a consenzienti). */
	TOPIC: 'TOPIC',
	/** Espansione ricorsiva gruppo→utenti, invio 1-a-1 (base contrattuale). */
	GROUP: 'GROUP',
});
export type CampaignTargetTypeValues = ValueOf<typeof CampaignTargetType>;

export const CampaignCategory = Object.freeze({
	SERVICE: 'SERVICE',
	MARKETING: 'MARKETING',
});
export type CampaignCategoryValues = ValueOf<typeof CampaignCategory>;

export const CampaignChannel = Object.freeze({
	PUSH: 'PUSH',
	EMAIL: 'EMAIL',
});
export type CampaignChannelValues = ValueOf<typeof CampaignChannel>;

// Cosa fare con gli utenti i cui device sono tutti stantii (>30gg) o assenti,
// per le campagne GROUP con canale PUSH. Configurabile per-campagna dall'admin.
export const StaleClientPolicy = Object.freeze({
	EMAIL_FALLBACK: 'EMAIL_FALLBACK',
	SKIP: 'SKIP',
});
export type StaleClientPolicyValues = ValueOf<typeof StaleClientPolicy>;

// Destinazione in-app al tap su notifica/email di campagna. Riusa i target
// già gestiti dal DeepLinkService del mobile (universal link app.yourfill.it).
export const CampaignDeepLinkType = Object.freeze({
	/** Nessuna navigazione: apre solo l'app. */
	NONE: 'NONE',
	/** Apre un contenuto/chapter CMS (deepLinkValue = chapterId). */
	CHAPTER: 'CHAPTER',
	/** Apre il dettaglio di una installazione (deepLinkValue = installationId). */
	INSTALLATION: 'INSTALLATION',
	/** Apre la lista notifiche in-app. */
	NOTIFICATIONS: 'NOTIFICATIONS',
});
export type CampaignDeepLinkTypeValues = ValueOf<typeof CampaignDeepLinkType>;

// Canale di provenienza di un click campagna (per i contatori aggregati).
export const ClickChannel = Object.freeze({
	EMAIL: 'email',
	PUSH: 'push',
});
export type ClickChannelValues = ValueOf<typeof ClickChannel>;

export interface ILocalizedText {
	it: string;
	en: string;
}

export interface INotificationCampaign extends IStringIdEntity {
	name: string;
	title: ILocalizedText;
	/** Testo breve: corpo push e/o incipit email. */
	body: ILocalizedText;
	/** Corpo HTML (Quill) per il canale EMAIL — sanitizzato lato backend. */
	emailBodyHtml?: Partial<ILocalizedText>;
	channels: CampaignChannelValues[];
	category: CampaignCategoryValues;
	targetType: CampaignTargetTypeValues;
	groupId?: number;
	/** NULL con status SCHEDULED = instant (claim immediato dello scheduler). */
	scheduledAt?: Date;
	status: CampaignStatusValues;
	staleClientPolicy: StaleClientPolicyValues;
	/** Sezione in-app aperta al tap (default NONE). */
	deepLinkType: CampaignDeepLinkTypeValues;
	/** Valore del target (chapterId / installationId); null per NONE/NOTIFICATIONS. */
	deepLinkValue?: string;
	/** Click aggregati ANONIMI da email e da push (incrementati dal backend). */
	clickCountEmail: number;
	clickCountPush: number;
	startedAt?: Date;
	completedAt?: Date;
	error?: string;
	/** Denormalizzato a fine run dal ledger: { sent, failed, skipped }. */
	stats?: ICampaignStats;
}

export interface ICampaignStats {
	sent: number;
	failed: number;
	skipped: number;
}

export interface ICreateNotificationCampaignDTO {
	name: string;
	title: ILocalizedText;
	body: ILocalizedText;
	emailBodyHtml?: Partial<ILocalizedText>;
	channels: CampaignChannelValues[];
	category: CampaignCategoryValues;
	targetType: CampaignTargetTypeValues;
	groupId?: number;
	scheduledAt?: Date | string;
	staleClientPolicy?: StaleClientPolicyValues;
	/** Sezione in-app aperta al tap (default NONE). */
	deepLinkType?: CampaignDeepLinkTypeValues;
	deepLinkValue?: string;
	/** true ⇒ status SCHEDULED (anche senza scheduledAt = instant), false ⇒ DRAFT. */
	schedule?: boolean;
}

/** Body del POST pubblico /api/public/notification-click. */
export interface INotificationClickDTO {
	/** Token firmato HMAC che cifra campaignId + canale (anonimo). */
	ct: string;
}

// =====================================================================
// Dispatch ledger — owner scritture: SOLO notification-service.
// Il backend lo legge per le stats/deliveries nel frontend_admin.
// =====================================================================

export const DispatchChannel = Object.freeze({
	PUSH: 'PUSH',
	EMAIL: 'EMAIL',
	/** Riga unica di broadcast su topic FCM (userId NULL). */
	TOPIC_PUSH: 'TOPIC_PUSH',
});
export type DispatchChannelValues = ValueOf<typeof DispatchChannel>;

export const DispatchStatus = Object.freeze({
	PENDING: 'PENDING',
	SENT: 'SENT',
	FAILED: 'FAILED',
	SKIPPED_NO_CONSENT: 'SKIPPED_NO_CONSENT',
	SKIPPED_NO_DEVICE: 'SKIPPED_NO_DEVICE',
	SKIPPED_STALE: 'SKIPPED_STALE',
	/** Soppiantato da una versione più recente nella finestra collapseKey. */
	COLLAPSED: 'COLLAPSED',
});
export type DispatchStatusValues = ValueOf<typeof DispatchStatus>;

export interface INotificationDispatch {
	id?: number;
	messageId: string;
	campaignId?: string;
	userId?: string;
	channel: DispatchChannelValues;
	status: DispatchStatusValues;
	error?: string;
	tokensTried: number;
	sentAt?: Date;
	createdAt?: Date;
}
